import AdminSpamCleanup from '@/modules/admin/client/components/AdminSpamCleanup';
import {
  createRootRoute,
  createRoute,
  createRouter,
  useParams,
  useNavigate,
  useRouterState,
  useSearch,
} from '@tanstack/react-router';
import React from 'react';

import {
  matchReactRoute,
  REACT_ROUTE_POLICIES,
  normalizePath,
  toTanStackPath,
} from '@/modules/core/shared/react-route-ownership.mts';
import Admin from '@/modules/admin/client/components/Admin.component';
import SupportInbox, {
  SupportMemberPage,
  SupportMemberSearchPage,
} from '@/modules/admin/client/components/Support.component';
import AdminCircles from '@/modules/admin/client/components/AdminCircles.component';
import AdminHackerspaces from '@/modules/admin/client/components/AdminHackerspaces.component';
import AdminStaffBlockers from '@/modules/admin/client/components/AdminStaffBlockers.component';
import AdminAcquisitionStories from '@/modules/admin/client/components/AdminAcquisitionStories.component';
import AdminAuditLog from '@/modules/admin/client/components/AdminAuditLog.component';
import AdminUsernameChanges from '@/modules/admin/client/components/AdminUsernameChanges.component';
import AdminMessages from '@/modules/admin/client/components/AdminMessages.component';
import AdminLocationCorrections from '@/modules/admin/client/components/AdminLocationCorrections.component';
import AdminNewsletter from '@/modules/admin/client/components/AdminNewsletter.component';
import AdminReferenceThreads from '@/modules/admin/client/components/AdminReferenceThreads.component';
import AdminExperienceChanges from '@/modules/admin/client/components/AdminExperienceChanges';
import ExperienceChangePage from '@/modules/experiences/client/components/ExperienceChangePage';
import AdminSearchUsers from '@/modules/admin/client/components/AdminSearchUsers.component';
import AdminThreads from '@/modules/admin/client/components/AdminThreads.component';
import AdminUser from '@/modules/admin/client/components/AdminUser.component';
import NotFoundPage from '@/modules/core/client/components/NotFoundPage.component';
import HomeRoute from '@/modules/pages/client/components/HomeRoute';
import Navigation from '@/modules/pages/client/components/Navigation.component';
import Contribute from '@/modules/pages/client/components/Contribute.component';
import Credits from '@/modules/pages/client/components/Credits.component';
import FaqBugsAndFeatures from '@/modules/pages/client/components/FaqBugsAndFeatures.component';
import FaqFoundation from '@/modules/pages/client/components/FaqFoundation.component';
import FaqGeneral from '@/modules/pages/client/components/FaqGeneral.component';
import FaqTechnology from '@/modules/pages/client/components/FaqTechnology.component';
import FaqTribes from '@/modules/pages/client/components/FaqTribes.component';
import Foundation from '@/modules/pages/client/components/Foundation.component';
import Guide from '@/modules/pages/client/components/Guide.component';
import Media from '@/modules/pages/client/components/Media.component';
import Privacy from '@/modules/pages/client/components/Privacy.component';
import Rules from '@/modules/pages/client/components/Rules.component';
import Safety from '@/modules/pages/client/components/Safety.component';
import Statistics from '@/modules/statistics/client/components/Statistics.component';
import SupportPage from '@/modules/support/client/components/SupportPage.component';
import Team from '@/modules/pages/client/components/Team.component';
import Greeters from '@/modules/pages/client/components/Greeters.component';
import Volunteering from '@/modules/pages/client/components/Volunteering.component';
import Welcome from '@/modules/users/client/components/Welcome.component';
import Inbox from '@/modules/messages/client/components/Inbox.component';
import Thread from '@/modules/messages/client/components/Thread.component';
import SearchUsers from '@/modules/search/client/components/SearchUsers.component';
import SearchPage from '@/modules/search/client/components/SearchPage.component';
import OfferShell from '@/modules/offers/client/components/OfferShell.component';
import OfferRedirectPage from '@/modules/offers/client/components/OfferRedirectPage.component';
import OfferHostPage from '@/modules/offers/client/components/OfferHostPage.component';
import OfferMeetListPage from '@/modules/offers/client/components/OfferMeetListPage.component';
import OfferMeetEditPage from '@/modules/offers/client/components/OfferMeetEditPage.component';
import SigninPage from '@/modules/users/client/components/SigninPage.component';
import SignupPage from '@/modules/users/client/components/SignupPage.component';
import ConfirmEmailPage from '@/modules/users/client/components/ConfirmEmailPage.component';
import ConfirmEmailInvalidPage from '@/modules/users/client/components/ConfirmEmailInvalidPage.component';
import ForgotPasswordPage from '@/modules/users/client/components/ForgotPasswordPage.component';
import ResetPasswordPage from '@/modules/users/client/components/ResetPasswordPage.component';
import ResetPasswordSuccessPage from '@/modules/users/client/components/ResetPasswordSuccessPage.component';
import ResetPasswordInvalidPage from '@/modules/users/client/components/ResetPasswordInvalidPage.component';
import RemoveProfilePage from '@/modules/users/client/components/RemoveProfilePage.component';
import ProfileSignupPage from '@/modules/users/client/components/ProfileSignupPage.component';
import ProfilePage from '@/modules/users/client/components/ProfilePage.component';
import ProfileEditAbout from '@/modules/users/client/components/ProfileEditAbout.component';
import ProfileEditLocations from '@/modules/users/client/components/ProfileEditLocations.component';
import ProfileEditPhoto from '@/modules/users/client/components/ProfileEditPhoto.component';
import ProfileEditNetworks from '@/modules/users/client/components/ProfileEditNetworks.component';
import ProfileEditAccount from '@/modules/users/client/components/ProfileEditAccount.component';
import ContactAddPage from '@/modules/contacts/client/components/ContactAddPage.component';
import ContactConfirmPage from '@/modules/contacts/client/components/ContactConfirmPage.component';
import TribesPage from '@/modules/tribes/client/components/TribesPage.component';
import TribeDetailPage from '@/modules/tribes/client/components/TribeDetailPage.component';
import { useAuth, type AuthUser } from '@/modules/core/client/react-app/auth';
import { useSettings } from '@/modules/core/client/react-app/app-context';
import ReactAppShell from './ReactAppShell';
import { signout } from './shell-helpers';

type RouteUser = AuthUser;
type RouteContext = {
  user: RouteUser | null;
  currentPath: string;
  params: Record<string, string>;
  navigate?: (path: string) => void;
};
type ClientRoute = {
  path: string;
  render: (context: RouteContext) => React.ReactNode;
};
export type RoutePolicy = {
  path: string;
  title: string;
  footerHidden?: boolean;
  footerVariant?: string;
  headerHidden?: boolean;
  redirectTo?: string;
  requiresAuth?: boolean;
  requiresRole?: string;
  [key: string]: unknown;
};

function renderWithUser(Component: React.ElementType) {
  return function renderRoute({ user }: RouteContext) {
    return React.createElement(Component, { user });
  };
}

function renderProfile({ currentPath, user }: RouteContext) {
  return (
    <ProfilePage
      currentPath={currentPath}
      user={user as React.ComponentProps<typeof ProfilePage>['user']}
    />
  );
}

function renderStatistics({ user }: RouteContext) {
  return React.createElement(Statistics, { isAuthenticated: Boolean(user) });
}

function renderOfferPage(Component: React.ElementType) {
  return function renderRoute({ user }: RouteContext) {
    return (
      <OfferShell user={user}>
        <Component user={user} />
      </OfferShell>
    );
  };
}

function NavigationRoute({ user }: RouteContext) {
  return (
    <Navigation
      onSignout={signout}
      user={user as React.ComponentProps<typeof Navigation>['user']}
    />
  );
}

function TribesPageRoute({ user }: RouteContext) {
  const { setUser } = useAuth();

  const handleMembershipUpdated = (
    data: { user?: Partial<NonNullable<RouteUser>> } | null,
  ) => {
    /* istanbul ignore else -- malformed membership callbacks cannot update auth state. */
    if (data?.user) {
      setUser({ ...user, ...data.user, roles: user?.roles });
    }
  };

  return (
    <TribesPage
      onMembershipUpdated={handleMembershipUpdated}
      user={user as React.ComponentProps<typeof TribesPage>['user']}
    />
  );
}

function TribeDetailPageRoute({
  user,
  circle,
}: {
  user: AuthUser | null;
  circle?: string;
}) {
  const { setUser } = useAuth();

  const handleMembershipUpdated = (
    data: { user?: Partial<NonNullable<RouteUser>> } | null,
  ) => {
    /* istanbul ignore else -- malformed membership callbacks cannot update auth state. */
    if (data?.user) {
      setUser({ ...user, ...data.user, roles: user?.roles });
    }
  };

  return (
    <TribeDetailPage
      circle={circle as string}
      onMembershipUpdated={handleMembershipUpdated}
      user={user as React.ComponentProps<typeof TribeDetailPage>['user']}
    />
  );
}

function ThreadRoute({ user }: RouteContext) {
  if (!user) return null;

  /* istanbul ignore next -- app settings always provide this default in production. */
  const { profileMinimumLength = 140 } = useSettings();

  return (
    <Thread
      profileMinimumLength={profileMinimumLength as number}
      user={user as React.ComponentProps<typeof Thread>['user']}
    />
  );
}

function renderCircleDetail({ user, params }: RouteContext) {
  return React.createElement(
    TribeDetailPageRoute as unknown as React.ComponentType<{
      user: AuthUser | null;
      circle?: string;
    }>,
    {
      user,
      circle: params.circle,
    },
  );
}

function renderExperienceChange({ params }: RouteContext) {
  return <ExperienceChangePage id={params.id} />;
}

const renderByPath: Record<string, (context: RouteContext) => React.ReactNode> =
  {
    '/': renderWithUser(HomeRoute),
    '/about': renderWithUser(HomeRoute),
    '/admin': () => <Admin />,
    '/admin/support': () => <SupportInbox />,
    '/admin/support/members': () => <SupportMemberSearchPage />,
    '/admin/support/requests/:requestId': ({ params }) => (
      <SupportInbox requestId={params.requestId} />
    ),
    '/admin/support/member/:username': ({ params }) => (
      <SupportMemberPage id={params.username} />
    ),
    '/admin/circles': ({ navigate }) => <AdminCircles onNavigate={navigate} />,
    '/admin/circles/:circle': ({ params, navigate }) => (
      <AdminCircles circleSlug={params.circle} onNavigate={navigate} />
    ),
    '/admin/hackerspaces': () => <AdminHackerspaces />,
    '/admin/staff-blockers': () => <AdminStaffBlockers />,
    '/admin/acquisition-stories': () => <AdminAcquisitionStories />,
    '/admin/audit-log': () => <AdminAuditLog />,
    '/admin/spam-cleanup': () => <AdminSpamCleanup />,
    '/admin/username-changes': () => <AdminUsernameChanges />,
    '/admin/messages': () => <AdminMessages />,
    '/admin/location-corrections': () => <AdminLocationCorrections />,
    '/admin/experience-changes': () => <AdminExperienceChanges />,
    '/admin/newsletter': () => <AdminNewsletter />,
    '/admin/reference-threads': () => <AdminReferenceThreads />,
    '/admin/search-users': () => <AdminSearchUsers />,
    '/admin/threads': () => <AdminThreads />,
    '/admin/user': ({ user }) => <AdminUser viewer={user as AuthUser} />,
    '/admin/user/:username': ({ params, user }) => (
      <AdminUser username={params.username} viewer={user as AuthUser} />
    ),
    '/circles': renderWithUser(TribesPageRoute),
    '/circles/:circle': renderCircleDetail,
    '/contact': renderWithUser(SupportPage),
    '/contribute': () => <Contribute />,
    '/faq': () => <FaqGeneral />,
    '/faq/bugs-and-features': () => <FaqBugsAndFeatures />,
    '/faq/circles': () => <FaqTribes />,
    '/faq/foundation': () => <FaqFoundation />,
    '/faq/technology': () => <FaqTechnology />,
    '/foundation': renderWithUser(Foundation),
    '/credits': () => <Credits />,
    '/guide': () => <Guide />,
    '/media': () => <Media />,
    '/messages': renderWithUser(Inbox),
    '/messages/:username': renderWithUser(ThreadRoute),
    '/navigation': renderWithUser(NavigationRoute),
    '/not-found': () => <NotFoundPage />,
    '/experiences/:id/change': renderExperienceChange,
    '/offer': () => <OfferRedirectPage />,
    '/offer/host': renderOfferPage(OfferHostPage),
    '/offer/meet': renderOfferPage(OfferMeetListPage),
    '/offer/meet/add': renderOfferPage(OfferMeetEditPage),
    '/offer/meet/:offerId': renderOfferPage(OfferMeetEditPage),
    '/password/forgot': () => <ForgotPasswordPage />,
    '/password/reset/invalid': () => <ResetPasswordInvalidPage />,
    '/password/reset/success': () => <ResetPasswordSuccessPage />,
    '/password/reset/:token': () => <ResetPasswordPage />,
    '/privacy': () => <Privacy />,
    '/profile-signup': () => <ProfileSignupPage />,
    '/profile/:username/experiences/new': renderWithUser(ProfilePage),
    '/profile/:username/experiences': renderWithUser(ProfilePage),
    '/profile/:username/accommodation': renderWithUser(ProfilePage),
    '/profile/:username/about': renderWithUser(ProfilePage),
    '/profile/:username/overview': renderWithUser(ProfilePage),
    '/profile/:username/contacts': renderWithUser(ProfilePage),
    '/profile/:username/tribes': renderWithUser(ProfilePage),
    '/profile/:username': renderProfile,
    '/profile/edit/locations': renderWithUser(ProfileEditLocations),
    '/profile/edit/photo': renderWithUser(ProfileEditPhoto),
    '/profile/edit/networks': renderWithUser(ProfileEditNetworks),
    '/profile/edit/account': renderWithUser(ProfileEditAccount),
    '/profile/edit': renderWithUser(ProfileEditAbout),
    '/contact-add/:userId': renderWithUser(ContactAddPage),
    '/contact-confirm/:contactId': renderWithUser(ContactConfirmPage),
    '/remove/:token': () => <RemoveProfilePage />,
    '/rules': () => <Rules />,
    '/safety': () => <Safety />,
    '/search': renderWithUser(SearchPage),
    '/search/members': () => <SearchUsers />,
    '/signin': () => <SigninPage />,
    '/signup': () => <SignupPage />,
    '/confirm-email/:token': () => <ConfirmEmailPage />,
    '/confirm-email-invalid': () => <ConfirmEmailInvalidPage />,
    '/statistics': renderStatistics,
    '/support': renderWithUser(SupportPage),
    '/team': renderWithUser(Team),
    '/team/greeters': renderWithUser(Greeters),
    '/volunteering': () => <Volunteering />,
    '/welcome': () => <Welcome />,
  };

export const routes = (REACT_ROUTE_POLICIES as RoutePolicy[]).map(route => ({
  ...route,
  render: renderByPath[route.path],
}));

function createClientRouteComponent(clientRoute: ClientRoute) {
  function ClientRouteComponent() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const params = useParams({ strict: false });
    const search = useSearch({ strict: false });
    const currentPath = useRouterState({
      select: state => state.location.pathname,
    });
    const remountSearch = { ...search };

    // Selecting an offer updates the URL without resetting the map viewport.
    if (clientRoute.path === '/search') {
      delete remountSearch.offer;
    }

    return (
      <React.Fragment key={JSON.stringify({ params, search: remountSearch })}>
        {clientRoute.render({
          currentPath,
          params,
          user,
          navigate: path => {
            void navigate({ to: path });
          },
        })}
      </React.Fragment>
    );
  }

  return ClientRouteComponent;
}

export const rootRoute = createRootRoute({
  component: ReactAppShell,
  notFoundComponent: NotFoundPage,
});

const profilePath = '/profile/:username';
const profileRouteConfig = routes.find(route => route.path === profilePath)!;
const profileRoute = createRoute({
  component: createClientRouteComponent(profileRouteConfig),
  getParentRoute: () => rootRoute,
  path: toTanStackPath(profilePath),
});

const profileTabRoutes = routes
  .filter(route => route.path.startsWith(`${profilePath}/`))
  .map(route =>
    createRoute({
      getParentRoute: () => profileRoute,
      path: toTanStackPath(route.path.slice(profilePath.length + 1)),
    }),
  );

const clientRoutes = routes
  .filter(
    route =>
      route.path !== profilePath && !route.path.startsWith(`${profilePath}/`),
  )
  .map(clientRoute =>
    createRoute({
      component: createClientRouteComponent(clientRoute),
      getParentRoute: () => rootRoute,
      path: toTanStackPath(clientRoute.path),
    }),
  );

export const routeTree = rootRoute.addChildren([
  ...clientRoutes,
  profileRoute.addChildren(profileTabRoutes),
]);

export function parseLegacySearch(search: string): Record<string, string> {
  const values: Record<string, string> = {};
  new URLSearchParams(search).forEach((value, key) => {
    values[key] = value;
  });
  return values;
}

export function stringifyLegacySearch(
  search: Record<string, string | number | boolean | null | undefined>,
): string {
  const searchParams = new URLSearchParams();

  Object.entries(search).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

export function createAppRouter() {
  return createRouter({
    context: {
      navigateOverride: undefined,
    },
    defaultPreload: 'intent',
    parseSearch: parseLegacySearch,
    routeTree,
    scrollRestoration: () => false,
    stringifySearch: stringifyLegacySearch,
  });
}

export function findRoute(path: string) {
  const matched = matchReactRoute(path);

  if (!matched?.policy) {
    return undefined;
  }

  return {
    ...matched.policy,
    params: matched.params,
    render: renderByPath[matched.policy.path],
  };
}

export { normalizePath };
